import { Component, Inject, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, tap } from 'rxjs/operators';

import { ConfigService } from '../../config.service';
import { CoreApiService, WriteResult, queryAll } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProfileService } from '../../profile/profile.service';
import { ProjectsService } from '../../projects/projects.service';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { BulkMoveSessions, BulkMoveSessionsConflictMode } from '../../shared/models/bulk.model';
import { Session } from '../../shared/models/session.model';
import { Subject as FwSubject } from '../../shared/models/subject.model';
import { replayValue } from '../../shared/observable.utils';
import { generateElementId } from '../../shared/string.utils';

type AcquisitionInput = Pick<Session, '_id' | 'label'>

interface DialogData {
  projectId?: string
  acquisitions: AcquisitionInput[]
  sourceSessionId: string
}

interface DialogResult {
  hasMoved: boolean
  hasDeleted: boolean
}

interface AcquisitionResult extends AcquisitionInput {
  error?: string
}

interface MoveSummary {
  total: number
  moved: number
  failed: number
  failedAcquisitions: AcquisitionResult[]
}

@Component({
  host: {
    'class': 'mat-dialog-component',
    'test-id': 'move-acquisitions-to-session-dialog',
  },
  selector: 'move-acquisitions-to-session-dialog',
  styleUrls: ['./move-acquisitions-to-session-dialog.component.scss'],
  templateUrl: './move-acquisitions-to-session-dialog.component.html',
})
export class MoveAcquisitionsToSessionDialog implements OnDestroy {
  auditTrailEnabled = false;
  elementIds = {
    existingSubjectLabel: generateElementId(),
    existingSessionLabel: generateElementId(),
    newSubjectLabel: generateElementId(),
    projectLabel: generateElementId(),
  }
  filteredSubjects$: Observable<FwSubject[]>
  filteredSessions$: Observable<Session[]>
  form: FormGroup
  groupedProjects$ = this.projectsService.getGroupedProjects([PermissionAction.ContainersCreateHierarchy])
  moveSummary: MoveSummary = null
  projectSubjects$ = new BehaviorSubject<FwSubject[]>([])
  projectSessions$ = new BehaviorSubject<Session[]>([])
  saving = false
  acquisitions = this.data.acquisitions
  hasConflictingAcquisitions = false
  updatingProject = false;
  updatingSubject = false;

  private subscriptions = {
    subjects: Subscription.EMPTY,
    sessions: Subscription.EMPTY,
    sessionChange: Subscription.EMPTY,
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: DialogData,
    private configService: ConfigService,
    private dialogRef: MatDialogRef<MoveAcquisitionsToSessionDialog, DialogResult>,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private profileService: ProfileService,
    private projectsService: ProjectsService,
  ) {
    this.form = new FormGroup({
      projectId: new FormControl(this.data.projectId || null, [Validators.required]),
      deleteEmptySource: new FormControl(true),
      existingSubjectId: new FormControl(null, [Validators.required, this.validSubjectId]),
      existingSessionId: new FormControl({ value: null, disabled: true }, [Validators.required, this.validSessionId, this.sameSession]),
    });

    this.auditTrailEnabled = this.configService.isFeatureEnabled('audit_trail');

    this.subscriptions.subjects = replayValue(this.form.get('projectId')).pipe(
      tap(() => {
        this.form.get('existingSubjectId').reset();
        this.form.get('existingSubjectId').disable();
        this.updatingProject = true;
        this.form.get('existingSessionId').reset();
        this.form.get('existingSessionId').disable();
      }),
      switchMap(projectId => projectId
        ? this.profileService.siteAdmin$.pipe(
          switchMap(isSiteAdmin => queryAll(params => this.coreApi.projects.subjects(projectId, {
            ...params,
            ...(isSiteAdmin ? { exhaustive: true } : {}),
          })))
        ) : of([]))
    ).subscribe(this.projectSubjects$);

    this.filteredSubjects$ = this.projectSubjects$.pipe(
      switchMap(subjects => {
        this.updatingProject = false;
        this.form.get('existingSubjectId').enable();
        return replayValue(this.form.get('existingSubjectId')).pipe(
          tap(subject => {
            this.form.get('existingSessionId')[subject ? 'enable' : 'disable']()
          }),
          map(subjectSearch => {
            subjectSearch = (subjectSearch || '').toLowerCase();
            return subjects.filter(subject => subject.label.toLowerCase().includes(subjectSearch) || subjectSearch === subject._id);
          })
        );
      }),
      map(filteredSubjects => filteredSubjects.slice(0, 20)),
    );

    this.subscriptions.sessions = replayValue(this.form.get('existingSubjectId')).pipe(
      filter(subjectId => Boolean(subjectId) && this.form.get('existingSubjectId').valid),
      switchMap(subjectId => {
        if (subjectId) {
          this.form.get('existingSessionId').disable();
          this.updatingSubject = true;

          return this.profileService.siteAdmin$.pipe(
            switchMap(isSiteAdmin => {
              return queryAll(() => this.coreApi.tree.query<Session>(
                {
                  sessions: {
                    fields: ['label', '_id'],
                  },
                },
                {
                  filter: `subject=${subjectId}`,
                  ...(isSiteAdmin ? { exhaustive: true } : {}),
                }
              ));
            }),
            tap(() => {
              this.form.get('existingSessionId').enable();
              this.updatingSubject = false;
            }));
        }
        else {
          return of([])
        }
    }),
    ).subscribe(this.projectSessions$);
    this.filteredSessions$ = this.projectSessions$.pipe(
      switchMap(sessions => {
        return replayValue(this.form.get('existingSessionId')).pipe(
          map(sessionSearch => {
            sessionSearch = (sessionSearch || '').toLowerCase();
            return sessions.filter(subject => subject.label.toLowerCase().includes(sessionSearch) || sessionSearch === subject._id);
          })
        );
      }),
      map(filteredSessions => filteredSessions.slice(0, 20)),
    );

    this.subscriptions.sessionChange = replayValue(this.form.get('existingSessionId')).pipe(
      tap(() => {
        this.hasConflictingAcquisitions = false;
      }),
      filter(session => Boolean(session) && this.form.get('existingSessionId').valid),
      debounceTime(200),
      switchMap(session => {
        const bulkMove: BulkMoveSessions = {
          destination_container_type: 'sessions',
          sources: this.data.acquisitions.map(acq => acq._id),
          destinations: [session],
          conflict_mode: BulkMoveSessionsConflictMode.DRY,
        };
        return this.coreApi.bulk.move.acquisitions(bulkMove);
      })
    ).subscribe({
      next: conflicts => {
        this.hasConflictingAcquisitions = conflicts.length > 0;
      },
      error: error => {
        this.notifications.error('session.moveAcquisitionToSession.dryError');
        this.logger.error(error);
      },
    })
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  displaySubjectLabel = (subjectId?: string): string => {
    const subject = this.projectSubjects$.value.find(sub => sub._id === subjectId);
    return subject?.label || subject?._id || '';
  }

  displaySessionLabel = (sessionId?: string): string => {
    const session = this.projectSessions$.value.find(sesh => sesh._id === sessionId);
    return session?.label || session?._id || '';
  }

  /**
   * If the user types a label without selecting an option, convert the label to an ID if possible
   */
  blurSubjectLabel(): void {
    const input = this.form.get('existingSubjectId').value;
    if (!input || this.projectSubjects$.value.some(sub => sub._id === input)) {
      return;
    }
    const subject = this.projectSubjects$.value.find(sub => sub.label === input);
    if (subject) {
      this.form.get('existingSubjectId').setValue(subject._id);
    }
  }

  blurSessionLabel(): void {
    const input = this.form.get('existingSessionId').value;
    if (!input || this.projectSessions$.value.some(sesh => sesh._id === input)) {
      return;
    }
    const session = this.projectSessions$.value.find(sesh => sesh.label === input);
    if (session) {
      this.form.get('existingSessionId').setValue(session._id);
    }
  }

  moveSessions(): void {
    if (!this.form.valid) {
      return;
    }
    this.saving = true;
    const destinationSession = this.form.get('existingSessionId').value;

    this.form.disable();
    
    const bulkMove: BulkMoveSessions = {
      destination_container_type: 'sessions',
      sources: this.data.acquisitions.map(acq => acq._id),
      destinations: [destinationSession],
      conflict_mode: BulkMoveSessionsConflictMode.MOVE,
    };
    this.coreApi.bulk.move.acquisitions(bulkMove).pipe(
      catchError(error => {
        this.notifications.error('session.moveAcquisitionToSession.error');
        this.form.enable();
        throw error;
      }),
      switchMap(() => {
        if (this.form.get('deleteEmptySource').value) {
          return queryAll(() => this.coreApi.sessions.acquisitions(this.data.sourceSessionId)).pipe(
            catchError(error => {
              this.unableToDeleteSession(error);
              return of(null);
            }),
            switchMap(acquisitions => {
              if (acquisitions?.length === 0) {
                const params = this.auditTrailEnabled ? { delete_reason: 'container_is_empty' } : {};
                return this.coreApi.containers.delete<WriteResult>(this.data.sourceSessionId, params).pipe(
                  catchError(error => {
                    this.unableToDeleteSession(error);
                    return of(null);
                  }),
                  tap(result => {
                    if (result.deleted > 0) {
                      this.notifications.success('session.moveAcquisitionToSession.sourceDelete.success');
                    }
                    else {
                      this.notifications.error('session.moveAcquisitionToSession.sourceDelete.error');
                    }
                  }),
                  switchMap(() => {
                    return of({ hasMoved: true, hasDeleted: true });
                  })
                )
              }
              return of({ hasMoved: true, hasDeleted: false });
            })
          );
        }
        return of({ hasMoved: true, hasDeleted: false });
      }),
    ).subscribe((result) => {
      this.dialogRef.close(result);
    });
  }

  unableToDeleteSession(error): void {
    this.logger.error(error);
    this.notifications.error('session.moveAcquisitionToSession.sourceDelete.error');
  }

  private validSubjectId: ValidatorFn = control => {
    if (control.value && !this.projectSubjects$.value.some(subject => subject._id === control.value)) {
      return { validSubjectId: 'Subject does not exist in project' };
    }
    return null;
  }

  private validSessionId: ValidatorFn = control => {
    if (control.value && !this.projectSessions$.value.some(session => session._id === control.value)) {
      return { validSessionId: 'Session does not exist in project' };
    }
    return null;
  }

  private sameSession: ValidatorFn = control => {
    if (control.value === this.data.sourceSessionId) {
      return { sameSession: 'Destination Session cannot be the same as the source Session' };
    }
    return null;
  }
}
